// redux imports
import store from 'store'
import { updatePermissions } from 'redux/Config'
// module imports
import { isFirefox } from 'modules/Browser'
// constant imports
import * as C from 'constants/Permissions'

// helpers
const isGranted = (state) => state === C.GRANTED
const isPrompt = (state) => state === C.PROMPT

// fallback permissions
const requestMicrophonePermissions = async () => {
  // check we can get stream
  let state = C.PROMPT
  const stopStream = (stream) => {
    stream &&
      stream.getAudioTracks().forEach((track) => {
        if (track.readyState === 'live') {
          track.stop()
        }
      })
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    if (!isFirefox) {
      stopStream(stream)
    } else {
      // Firefox does not remember the permission given by default
      // So it causes double permission popup prompt when jsSIP asks for a stream
      // Delaying the stream elimination will fix double prompt
      // TODO: properly pass this stream to jsSIP session
      setTimeout(() => stopStream(stream), 1000)
    }
    state = C.GRANTED
  } catch (e) {
    state = C.DENIED
  }
  return state
}

const requestNotificationsPermissions = async () => {
  if (!('Notification' in window)) {
    return C.DENIED
  }

  let state
  const updatePermission = (permission) => permission

  try {
    state = await Notification.requestPermission().then(updatePermission)
  } catch (e) {
    state = await Notification.requestPermission(updatePermission)
  }

  return state.replace(C.DEFAULT, C.PROMPT)
}

const requestPermissions = async (name) => {
  let state
  try {
    if (name === C.MICROPHONE) {
      state = await requestMicrophonePermissions()
    } else if (name === C.NOTIFICATIONS) {
      state = await requestNotificationsPermissions()
    }
    return state || C.GRANTED
  } catch (e) {
    return C.DENIED
  }
}

// needed since prompting triggers on change
let prompted = false

// prompt the user for permission
const prompt = async (name) => {
  let state
  prompted = true
  try {
    state = await requestPermissions(name)
    return isGranted(state)
  } catch (e) {
    return false
  }
}

// handle permission changes
const onChange = async (name, newState) => {
  if (prompted) {
    prompted = false
    if (!isFirefox) return
  }

  let state = newState

  if (!isPrompt(state)) {
    state = isGranted(state)
  } else {
    state = null
  }

  store.dispatch(updatePermissions({ name, state }))
}

export const subscribeToPermissionChanges = () => {
  const permissions = [C.MICROPHONE, C.NOTIFICATIONS]

  return Promise.all(
    permissions.map(async (name) => {
      let permissionStatus
      try {
        permissionStatus = await navigator.permissions.query({ name })
        permissionStatus.onchange = (e) => {
          onChange(name, e?.target?.state)
        }
      } catch (e) {
        permissionStatus = null
      }
      return permissionStatus
    }),
  )
}

// get the initial permission
export const getPermission = async (name) => {
  let state

  // get status
  try {
    const permissionStatus = await navigator.permissions.query({ name })
    state = permissionStatus.state.replace(C.DEFAULT, C.PROMPT)
  } catch (e) {
    // fallback for firefox
    state = await requestPermissions(name)
  }

  // return prompted value if necessary
  if (isPrompt(state)) {
    return prompt(name)
  }

  // return status
  return isGranted(state)
}

// main function to get all permissions
const getPermissions = async () => {
  const permissions = {
    [C.MICROPHONE]: await getPermission(C.MICROPHONE),
    [C.NOTIFICATIONS]: await getPermission(C.NOTIFICATIONS),
  }
  return Promise.resolve(permissions)
}

export default getPermissions
